import React from 'react';
import { Link } from 'react-router-dom';
import useDocumentTitle from '../../hooks/useDocumentTitle';

const TermsPage: React.FC = () => {
  useDocumentTitle('Terms and Conditions');

  return (
    <div style={{ minHeight: '100vh', background: '#f7fafc' }}>
      {/* Header */}
      <div className="border-bottom bg-white">
        <div className="container py-3">
          <div className="d-flex align-items-center justify-content-between">
            <Link to="/" className="text-decoration-none d-flex align-items-center">
              <div style={{
                width: 40, height: 40,
                background: 'linear-gradient(135deg, #06b6d4, #3b82f6)',
                borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <span style={{ color: '#fff', fontWeight: 700, fontSize: '1.2rem' }}>C</span>
              </div>
              <span className="ms-2 fw-bold" style={{
                fontSize: '1.2rem',
                background: 'linear-gradient(135deg, #06b6d4, #3b82f6)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>CloudAyn</span>
            </Link>
            <Link to="/" className="btn btn-outline-primary btn-sm">Back to Home</Link>
          </div>
        </div>
      </div>

      <div className="container py-5" style={{ maxWidth: '800px' }}>
        <h1 className="fw-bold mb-2">Terms and Conditions</h1>
        <p className="text-muted mb-4">Last updated: 27 February 2026</p>

        <div className="card border-0 shadow-sm p-4 p-md-5" style={{ lineHeight: '1.7' }}>
          <section className="mb-4">
            <h4 className="fw-bold mb-3">1. Acceptance of Terms</h4>
            <p>
              These Terms and Conditions ("Terms") constitute a legally binding agreement between you ("User", "you", or "your")
              and <strong>INFIYO TECHNOLOGIES WLL</strong>, a company registered in the Kingdom of Bahrain (Commercial Registration
              No. applicable), operating under the brand name <strong>CloudAyn</strong> ("Company", "we", "us", or "our").
            </p>
            <p>
              By creating an account, accessing, or using the CloudAyn platform and any associated services, you acknowledge
              that you have read, understood, and agree to be bound by these Terms and our{' '}
              <Link to="/privacy">Privacy Policy</Link>. If you do not agree to these Terms, you must immediately
              discontinue use of the platform.
            </p>
            <p>
              If you are using the platform on behalf of an organization, you represent and warrant that you have the authority
              to bind that organization to these Terms, and references to "you" shall include both you as an individual and the
              organization you represent.
            </p>
          </section>

          <section className="mb-4">
            <h4 className="fw-bold mb-3">2. Description of Services</h4>
            <p>
              CloudAyn is a cloud-based Software as a Service (SaaS) platform that provides a comprehensive suite of digital
              asset management, monitoring, and cybersecurity tools. Our services include but are not limited to:
            </p>
            <ul>
              <li><strong>Web Analytics:</strong> Real-time website traffic analytics, visitor tracking, page view analysis, traffic source identification, device and browser statistics, and custom event tracking</li>
              <li><strong>Website Monitoring:</strong> 24/7 uptime monitoring, response time measurement, downtime incident tracking, and performance reporting</li>
              <li><strong>Cybersecurity Auditing:</strong> Automated vulnerability scanning, SSL/TLS analysis, security header checks, port scanning, malware detection, and AI-powered security advisory</li>
              <li><strong>Server Monitoring:</strong> Server health metrics (CPU, memory, disk, network), event logging, and alerting</li>
              <li><strong>Email Security Monitoring:</strong> DNS record validation (SPF, DKIM, DMARC), blacklist monitoring, and email deliverability insights</li>
              <li><strong>Alerts and Notifications:</strong> Configurable alert rules for downtime, security issues, performance degradation, and server events delivered via email</li>
              <li><strong>Reporting and Exports:</strong> Downloadable PDF reports, analytics dashboards, and data export capabilities</li>
              <li><strong>API Access:</strong> RESTful API and JavaScript SDK for integration with your websites and applications</li>
              <li><strong>Team Collaboration:</strong> Multi-user organizations with role-based access control and team invitations</li>
            </ul>
            <p>
              We reserve the right to modify, suspend, or discontinue any feature or service at any time with reasonable
              notice. New features or tools added to the platform shall also be subject to these Terms.
            </p>
          </section>

          <section className="mb-4">
            <h4 className="fw-bold mb-3">3. Account Registration and Authentication</h4>
            <p>
              To use CloudAyn, you must create an account by providing accurate, current, and complete information. You are
              responsible for maintaining the accuracy of your account details and for all activities that occur under your account.
            </p>
            <p>
              CloudAyn offers multiple authentication methods for your convenience and security:
            </p>
            <ul>
              <li><strong>Email OTP (One-Time Password):</strong> Passwordless authentication via a verification code sent to your registered email address</li>
              <li><strong>Google Sign-In:</strong> Authentication using your Google account credentials (when enabled)</li>
              <li><strong>Microsoft Sign-In:</strong> Authentication using your Microsoft account credentials (when enabled)</li>
            </ul>
            <p>
              When you sign in using a third-party provider (Google or Microsoft), we receive basic profile information
              (name and email address) from that provider to create or authenticate your account. We do not receive or
              store your third-party passwords.
            </p>
            <p>
              You must be at least 18 years of age or the age of legal majority in your jurisdiction to create an account.
              You agree to immediately notify us of any unauthorized use of your account or any security breach.
            </p>
          </section>

          <section className="mb-4">
            <h4 className="fw-bold mb-3">4. Subscription Plans and Pricing</h4>
            <p>
              CloudAyn offers multiple subscription tiers designed to meet different business needs, including a Free plan
              and paid plans (Starter, Professional, and Enterprise). Each plan includes specific feature sets, usage limits
              (number of assets, team members, data retention period), and service levels.
            </p>
            <p>
              <strong>Billing:</strong> Paid subscriptions are billed on a monthly or annual basis, as selected at the time
              of purchase. Annual subscriptions are offered at a discounted rate. All prices are quoted in United States
              Dollars (USD) unless otherwise stated.
            </p>
            <p>
              <strong>Free Trial:</strong> Certain paid plans may include a complimentary trial period. During the trial,
              you will have access to the full features of the selected plan. At the end of the trial period, your subscription
              will <strong>not</strong> automatically convert to a paid subscription. You must explicitly choose to subscribe
              to continue using paid features.
            </p>
            <p>
              <strong>Payment Methods:</strong> Payments are processed through secure third-party payment gateways, which
              may include Tap Payments (credit/debit cards), PayPal, and bank transfers. By providing payment information,
              you authorize us to charge the applicable subscription fees. The availability of payment methods may vary
              and is subject to change.
            </p>
            <p>
              <strong>Taxes:</strong> All subscription fees are exclusive of applicable taxes. If your organization is
              registered in the Kingdom of Bahrain, Value Added Tax (VAT) at the prevailing rate (currently 10%) will be
              applied in accordance with Bahrain National Bureau for Revenue regulations. You are responsible for any
              applicable taxes in your jurisdiction.
            </p>
            <p>
              <strong>Price Changes:</strong> We reserve the right to change subscription pricing with at least 30 days'
              advance notice. Price changes will take effect at the start of your next billing cycle following the notice period.
            </p>
          </section>

          <section className="mb-4">
            <h4 className="fw-bold mb-3">5. Refund and Cancellation Policy</h4>
            <p>
              All subscription payments are <strong>non-refundable</strong> for the active subscription period. Once a subscription
              is activated and payment has been processed, no refunds will be issued for the remaining duration of that billing cycle.
            </p>
            <p>
              You may cancel your subscription at any time through your account settings or by contacting our support team.
              Cancellation will take effect at the end of the current billing cycle, and you will retain access to paid
              features until that date. No prorated refunds will be issued for unused portions of the billing period.
            </p>
            <p>
              Upon cancellation, your account will be downgraded to the Free plan. Your data will be retained for 30 days
              following cancellation, after which it may be permanently deleted.
            </p>
            <p>
              For complete details, please refer to our <Link to="/refund-policy">Refund Policy</Link>.
            </p>
          </section>

          <section className="mb-4">
            <h4 className="fw-bold mb-3">6. Acceptable Use Policy</h4>
            <p>You agree to use CloudAyn in a lawful, ethical, and responsible manner. You shall not:</p>
            <ul>
              <li>Use the services for any unlawful purpose or in violation of any applicable local, national, or international laws or regulations</li>
              <li>Attempt to gain unauthorized access to any part of the platform, other users' accounts, or any systems or networks connected to the platform</li>
              <li>Use the platform to scan, monitor, or perform security tests on websites, servers, or systems you do not own or have explicit written authorization to test</li>
              <li>Reverse engineer, decompile, disassemble, or attempt to derive the source code of any part of the platform</li>
              <li>Use the services to distribute malware, viruses, spam, phishing, or any other harmful or deceptive content</li>
              <li>Resell, sublicense, lease, or redistribute the services or any part thereof without our prior written consent</li>
              <li>Intentionally exceed the usage limits of your subscription plan through automated or manual means</li>
              <li>Use the platform in any manner that could damage, disable, overburden, or impair our servers or networks</li>
              <li>Collect or harvest personal information of other users without their consent</li>
              <li>Use our API or SDK in a manner inconsistent with its documentation or intended purpose</li>
            </ul>
            <p>
              Violation of this Acceptable Use Policy may result in immediate suspension or termination of your account
              without notice or refund.
            </p>
          </section>

          <section className="mb-4">
            <h4 className="fw-bold mb-3">7. Data Ownership and Privacy</h4>
            <p>
              <strong>Your Data:</strong> You retain full ownership of all data you submit to or generate through the
              CloudAyn platform, including analytics data, monitoring data, and security scan results. We claim no
              intellectual property rights over your content or data.
            </p>
            <p>
              <strong>Our Use of Data:</strong> We access and process your data solely to provide, maintain, and improve
              the CloudAyn services. We do not sell, rent, or trade your data to third parties for marketing purposes.
              We may use aggregated, anonymized, and de-identified data for statistical analysis, benchmarking, and
              service improvement.
            </p>
            <p>
              <strong>Privacy:</strong> Your use of CloudAyn is governed by our <Link to="/privacy">Privacy Policy</Link>,
              which describes in detail how we collect, use, store, and protect your information. By using the platform,
              you consent to the data practices described in our Privacy Policy.
            </p>
            <p>
              <strong>Analytics Tracking Code:</strong> When you install the CloudAyn tracking code on your websites, you
              are responsible for ensuring that you have appropriate user consent and privacy notices in place for your
              website visitors, as required by applicable data protection laws (including GDPR, PDPL, and similar regulations).
            </p>
          </section>

          <section className="mb-4">
            <h4 className="fw-bold mb-3">8. Intellectual Property</h4>
            <p>
              The CloudAyn platform, including its software, source code, algorithms, user interface design, graphics,
              logos, trademarks, documentation, and all related content, is the exclusive intellectual property of
              INFIYO TECHNOLOGIES WLL and is protected by applicable copyright, trademark, and other intellectual property laws.
            </p>
            <p>
              Your subscription grants you a limited, non-exclusive, non-transferable, revocable license to access and use
              the platform for its intended purpose during the term of your subscription. This license does not grant you
              any right to modify, copy, distribute, or create derivative works based on the platform.
            </p>
            <p>
              The CloudAyn name, logo, and all related names, logos, product and service names, designs, and slogans are
              trademarks of INFIYO TECHNOLOGIES WLL. You may not use these marks without our prior written permission.
            </p>
          </section>

          <section className="mb-4">
            <h4 className="fw-bold mb-3">9. Third-Party Services and Integrations</h4>
            <p>
              CloudAyn integrates with and relies upon third-party services including, but not limited to, payment
              processors (Tap Payments, PayPal), authentication providers (Google, Microsoft), cloud infrastructure
              providers, and email delivery services. Your use of these third-party services is subject to their
              respective terms of service and privacy policies.
            </p>
            <p>
              We are not responsible for the availability, accuracy, or content of third-party services, and we shall
              not be liable for any loss or damage arising from your use of or reliance on such services.
            </p>
          </section>

          <section className="mb-4">
            <h4 className="fw-bold mb-3">10. Service Availability and Warranties</h4>
            <p>
              We strive to maintain high availability and reliability of the CloudAyn platform. However, we do not
              guarantee uninterrupted, error-free, or completely secure operation of the services.
            </p>
            <p>
              THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS,
              IMPLIED, OR STATUTORY, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR
              A PARTICULAR PURPOSE, ACCURACY, AND NON-INFRINGEMENT.
            </p>
            <p>
              We do not warrant that: (a) the services will meet your specific requirements; (b) the services will be
              uninterrupted, timely, secure, or error-free; (c) the results obtained from the use of the services
              (including security scan results) will be accurate or reliable; or (d) any errors in the platform
              will be corrected.
            </p>
            <p>
              <strong>Scheduled Maintenance:</strong> We may perform scheduled maintenance that may temporarily affect
              service availability. We will endeavor to provide advance notice and schedule maintenance during low-usage periods.
            </p>
          </section>

          <section className="mb-4">
            <h4 className="fw-bold mb-3">11. Limitation of Liability</h4>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, INFIYO TECHNOLOGIES WLL, ITS OFFICERS, DIRECTORS,
              EMPLOYEES, AGENTS, AFFILIATES, AND LICENSORS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
              CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO:
            </p>
            <ul>
              <li>Loss of profits, revenue, or business opportunities</li>
              <li>Loss of data or data breach consequences</li>
              <li>Loss of goodwill or reputation</li>
              <li>Cost of procurement of substitute services</li>
              <li>Any damages arising from unauthorized access to or alteration of your data</li>
              <li>Any damages arising from reliance on security scan results or monitoring data</li>
            </ul>
            <p>
              IN NO EVENT SHALL OUR TOTAL AGGREGATE LIABILITY FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THESE TERMS
              OR YOUR USE OF THE PLATFORM EXCEED THE TOTAL AMOUNT PAID BY YOU TO US IN THE TWELVE (12) MONTHS IMMEDIATELY
              PRECEDING THE EVENT GIVING RISE TO THE CLAIM, OR ONE HUNDRED US DOLLARS (USD $100), WHICHEVER IS GREATER.
            </p>
            <p>
              The limitations in this section apply regardless of the legal theory on which the claim is based, including
              contract, tort (including negligence), strict liability, or any other basis.
            </p>
          </section>

          <section className="mb-4">
            <h4 className="fw-bold mb-3">12. Indemnification</h4>
            <p>
              You agree to indemnify, defend, and hold harmless INFIYO TECHNOLOGIES WLL and its officers, directors,
              employees, and agents from and against any and all claims, liabilities, damages, losses, costs, and
              expenses (including reasonable legal fees) arising out of or related to: (a) your use of the platform;
              (b) your violation of these Terms; (c) your violation of any third-party rights; or (d) your use of the
              analytics tracking code on your websites without appropriate visitor consent.
            </p>
          </section>

          <section className="mb-4">
            <h4 className="fw-bold mb-3">13. Termination</h4>
            <p>
              <strong>By You:</strong> You may terminate your account at any time by canceling your subscription and
              contacting our support team at info@cloudayn.com. Termination will take effect at the end of the current
              billing cycle.
            </p>
            <p>
              <strong>By Us:</strong> We may suspend or terminate your access to the platform immediately, without prior
              notice or liability, if: (a) you breach any provision of these Terms; (b) you fail to pay subscription fees
              when due; (c) we are required to do so by law; or (d) we reasonably believe your actions may cause legal
              liability or harm to the platform or other users.
            </p>
            <p>
              <strong>Effect of Termination:</strong> Upon termination, your right to access and use the platform ceases
              immediately. We may retain certain data as required by law, for dispute resolution, or for legitimate
              business purposes (such as payment records for tax compliance). Analytics and monitoring data may be
              deleted within 30 days of account termination.
            </p>
          </section>

          <section className="mb-4">
            <h4 className="fw-bold mb-3">14. Modifications to Terms</h4>
            <p>
              We reserve the right to modify these Terms at any time. If we make material changes, we will notify you by
              email to the address associated with your account and/or by posting a prominent notice on the platform at
              least 15 days before the changes take effect.
            </p>
            <p>
              Your continued use of the platform after the effective date of the revised Terms constitutes your acceptance
              of the changes. If you do not agree with the modified Terms, you must stop using the platform and cancel
              your subscription.
            </p>
          </section>

          <section className="mb-4">
            <h4 className="fw-bold mb-3">15. Governing Law and Dispute Resolution</h4>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the Kingdom of Bahrain,
              without regard to its conflict of law provisions.
            </p>
            <p>
              Any dispute, controversy, or claim arising out of or relating to these Terms or the use of the platform
              shall first be attempted to be resolved through good-faith negotiation between the parties. If the dispute
              cannot be resolved through negotiation within 30 days, it shall be submitted to the exclusive jurisdiction
              of the competent courts of the Kingdom of Bahrain.
            </p>
          </section>

          <section className="mb-4">
            <h4 className="fw-bold mb-3">16. General Provisions</h4>
            <p>
              <strong>Entire Agreement:</strong> These Terms, together with the Privacy Policy and Refund Policy,
              constitute the entire agreement between you and INFIYO TECHNOLOGIES WLL regarding the use of CloudAyn.
            </p>
            <p>
              <strong>Severability:</strong> If any provision of these Terms is held to be invalid or unenforceable,
              the remaining provisions shall continue in full force and effect.
            </p>
            <p>
              <strong>Waiver:</strong> Our failure to enforce any right or provision of these Terms shall not constitute
              a waiver of that right or provision.
            </p>
            <p>
              <strong>Assignment:</strong> You may not assign or transfer these Terms or your rights hereunder without
              our prior written consent. We may assign these Terms without restriction.
            </p>
            <p>
              <strong>Force Majeure:</strong> We shall not be liable for any delay or failure to perform resulting from
              causes beyond our reasonable control, including but not limited to acts of God, natural disasters, war,
              terrorism, pandemics, government actions, or failures of third-party services.
            </p>
          </section>

          <section>
            <h4 className="fw-bold mb-3">17. Contact Information</h4>
            <p>
              If you have any questions, concerns, or feedback regarding these Terms, please contact us:
            </p>
            <ul className="list-unstyled">
              <li><strong>Company:</strong> INFIYO TECHNOLOGIES WLL</li>
              <li><strong>Brand:</strong> CloudAyn</li>
              <li><strong>Email:</strong> <a href="mailto:info@cloudayn.com">info@cloudayn.com</a></li>
              <li><strong>Website:</strong> <a href="https://cloudayn.com" target="_blank" rel="noopener noreferrer">https://cloudayn.com</a></li>
              <li><strong>Location:</strong> Kingdom of Bahrain</li>
            </ul>
          </section>
        </div>

        {/* Footer links */}
        <div className="text-center mt-4">
          <Link to="/privacy" className="text-muted me-3">Privacy Policy</Link>
          <Link to="/refund-policy" className="text-muted">Refund Policy</Link>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
